import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper'
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { MContentHeader, resolveRawButton } from '../../molecules/MContentHeader/index';
import { MContentHeaderAlignment } from '../../molecules/MContentHeader/MContentHeader.types';
import { MCarouselControls } from '../../molecules/MCarouselControls/index';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';
import { MSlot } from '../../molecules/MSlot/index';
import { useDirection } from '../../hooks';

import * as Styled from './OCardCarousel.styles';
import {
  DESKTOP_CARD_COLS,
  DESKTOP_PEEK_FRACTION,
  GRID_COLUMNS,
  MOBILE_CARD_COLS,
  TABLET_CARD_COLS,
} from './OCardCarousel.styles';
import type { OCardCarouselCqItem, OCardCarouselProps } from './OCardCarousel.types';

const COMPONENT_NAME = 'o-web-ui-kit-OCardCarousel';
const TEST_ID = 'web-ui-kit-OCardCarousel';

const SUPPORTED_DESKTOP_CARDS = [1, 2, 3, 4, 6] as const;
const DEFAULT_DESKTOP_CARDS = 3;

const parseDesktopCards = (value?: string): number => {
  const parsed = parseInt(value ?? '', 10);
  return SUPPORTED_DESKTOP_CARDS.includes(parsed as (typeof SUPPORTED_DESKTOP_CARDS)[number])
    ? parsed
    : DEFAULT_DESKTOP_CARDS;
};

const parseBoolean = (value: boolean | string | undefined): boolean => value === true || value === 'true';

const parseTotalNoOfCards = (value: number | string | undefined): number => {
  const parsed = typeof value === 'number' ? value : parseInt(value ?? '', 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.floor(parsed);
};

const getOrderedItemKeys = (items: Record<string, unknown>, order?: string[]): string[] => {
  if (order?.length) {
    return order.filter(itemKey => itemKey in items);
  }
  return Object.keys(items);
};

/** Mobile: each card takes 10 of 12 grid columns. */
const getMobilePerView = (): number => GRID_COLUMNS / MOBILE_CARD_COLS;

/** Tablet: each card takes 5 of 12 grid columns. */
const getTabletPerView = (): number => GRID_COLUMNS / TABLET_CARD_COLS;

/**
 * Desktop slidesPerView from the shared 12/desktopCards lookup.
 *   1 -> 1, 2 -> 2, 3 -> 3, 4 -> 4, 6 -> 6
 */
const getDesktopPerView = (desktopCardsNum: number, peeking: boolean): number => {
  const base = GRID_COLUMNS / (DESKTOP_CARD_COLS[desktopCardsNum] ?? 4);
  return peeking ? base + DESKTOP_PEEK_FRACTION : base;
};

const getCardsPerPageFromSlidesPerView = (
  slidesPerView: SwiperType['params']['slidesPerView'],
  defaultCardsPerPage: number
): number => {
  return typeof slidesPerView === 'number' ? Math.max(1, Math.floor(slidesPerView)) : defaultCardsPerPage;
};

export const OCardCarousel: React.FC<OCardCarouselProps> = ({
  desktopCards,
  peeking,
  noOfTotalCards,
  mContentHeader,
  mCarouselControls,
  pagePath = '',
  itemPath = '',
  aemReactComponentMapping = {},
  isAuthorMode = false,
  children,
  className = '',
  model,
  ':items': aemItems,
  ':itemsOrder': aemItemsOrder,
  cqItems,
  cqItemsOrder,
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const swiperWrapperRef = useRef<HTMLDivElement | null>(null);
  const { direction } = useDirection();

  const resolvedDesktopCards = desktopCards ?? (model?.['desktopCards'] as string | undefined) ?? '3';
  const resolvedPeeking = parseBoolean(peeking ?? (model?.['peeking'] as string | boolean | undefined));
  const resolvedTotalNoOfCards = noOfTotalCards ?? (model?.['noOfTotalCards'] as number | string | undefined);
  const resolvedMContentHeader = (mContentHeader ?? model?.['mContentHeader']) as OCardCarouselProps['mContentHeader'];
  const resolvedMCarouselControls = (mCarouselControls ??
    model?.['mCarouselControls']) as OCardCarouselProps['mCarouselControls'];
  const resolvedPagePath = pagePath || (model?.['pagePath'] as string | undefined) || '';
  const resolvedItemPath = itemPath || (model?.['itemPath'] as string | undefined) || '';
  const desktopCardsNum = parseDesktopCards(resolvedDesktopCards);
  const totalCardSlots = parseTotalNoOfCards(resolvedTotalNoOfCards);

  const authoredItems = (aemItems ?? cqItems ?? model?.[':items'] ?? model?.['cqItems'] ?? {}) as Record<
    string,
    OCardCarouselCqItem
  >;
  const authoredOrder =
    aemItemsOrder ?? cqItemsOrder ?? model?.[':itemsOrder'] ?? model?.['cqItemsOrder'] ?? Object.keys(authoredItems);
  const authoredItemKeys = useMemo(
    () => getOrderedItemKeys(authoredItems, authoredOrder),
    [authoredItems, authoredOrder]
  );
  const renderedSlotKeys = useMemo(
    () =>
      totalCardSlots > 0 ? Array.from({ length: totalCardSlots }, (_, index) => `item${index}`) : authoredItemKeys,
    [authoredItemKeys, totalCardSlots]
  );
  const renderedSlotCount = renderedSlotKeys.length;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsPerControlPage, setCardsPerControlPage] = useState(desktopCardsNum);

  const childSlides = useMemo(() => (children ? React.Children.toArray(children) : []), [children]);
  const hasChildren = childSlides.length > 0;

  const visualSlideCount = hasChildren ? childSlides.length : renderedSlotCount;
  const hasSlides = visualSlideCount > 0;
  const hasHeader = Boolean(resolvedMContentHeader);
  const controlSlideCount = hasChildren ? childSlides.length : renderedSlotCount;
  const isOnlyOneSlide = visualSlideCount <= 1;
  const isDesktopSingleCard = isOnlyOneSlide || (desktopCardsNum === 1 && !resolvedPeeking);
  // Tablet special case: with exactly 2 slides both cards take 6/12 cols
  // (slidesPerView=2) instead of the default 2.4/view, avoiding an empty sliver.
  const isTwoSlideTabletGrid = visualSlideCount === 2;
  const desktopPeeking = !isOnlyOneSlide && resolvedPeeking && visualSlideCount > desktopCardsNum;
  const effectiveCardsPerControlPage = Math.min(Math.max(cardsPerControlPage, 1), Math.max(controlSlideCount, 1));
  const pageStartIndexes = useMemo(() => {
    if (controlSlideCount <= 0) return [0];
    const perPage = Math.max(1, effectiveCardsPerControlPage);
    const lastReachableStart = Math.max(0, controlSlideCount - perPage);
    const starts: number[] = [];
    for (let index = 0; index < lastReachableStart; index += perPage) {
      starts.push(index);
    }
    if (starts.length === 0 || starts[starts.length - 1] !== lastReachableStart) {
      starts.push(lastReachableStart);
    }
    return starts;
  }, [controlSlideCount, effectiveCardsPerControlPage]);

  const controlsType = (() => {
    const raw = String(resolvedMCarouselControls?.type ?? '').toLowerCase();
    return raw === MCarouselControlsType.Numbers || raw === MCarouselControlsType.Digits
      ? MCarouselControlsType.Numbers
      : MCarouselControlsType.Dots;
  })();
  const imageOverlay = parseBoolean(resolvedMCarouselControls?.imageOverlay);
  const showArrows =
    resolvedMCarouselControls?.arrows === undefined ? true : parseBoolean(resolvedMCarouselControls.arrows);

  const totalControlSlides = pageStartIndexes.length;
  const currentControlSlide = pageStartIndexes.reduce((closestPageIndex, startIndex, pageIndex) => {
    const currentDistance = Math.abs(currentSlide - startIndex);
    const closestDistance = Math.abs(currentSlide - (pageStartIndexes[closestPageIndex] ?? 0));
    return currentDistance <= closestDistance ? pageIndex : closestPageIndex;
  }, 0);

  // Controls hide at any breakpoint where every slide already fits in view,
  // so each check mirrors the slidesPerView used by Swiper at that breakpoint.
  const needsPagingMobile = controlSlideCount > 1 && controlSlideCount > getMobilePerView();
  const needsPagingTablet =
    controlSlideCount > 1 && controlSlideCount > (controlSlideCount === 2 ? 2 : getTabletPerView());
  const desktopSlidesPerView = isDesktopSingleCard ? 1 : getDesktopPerView(desktopCardsNum, desktopPeeking);
  const needsPagingDesktop = controlSlideCount > 1 && controlSlideCount > desktopSlidesPerView;
  const showControls = controlSlideCount > 1 && (needsPagingMobile || needsPagingTablet || needsPagingDesktop);

  const handlePrev = () => {
    swiperRef.current?.slideTo(pageStartIndexes[Math.max(currentControlSlide - 1, 0)] ?? 0);
  };

  const handleNext = () => {
    swiperRef.current?.slideTo(pageStartIndexes[Math.min(currentControlSlide + 1, totalControlSlides - 1)] ?? 0);
  };

  const syncSwiperState = useCallback(
    (swiper: SwiperType) => {
      setCurrentSlide(swiper.activeIndex);
      setCardsPerControlPage(getCardsPerPageFromSlidesPerView(swiper.params.slidesPerView, desktopCardsNum));
    },
    [desktopCardsNum]
  );

  useEffect(() => {
    if (swiperRef.current) {
      syncSwiperState(swiperRef.current);
    } else {
      setCardsPerControlPage(desktopCardsNum);
    }
  }, [desktopCardsNum, visualSlideCount, syncSwiperState]);

  // Desktop peeking lets the wrapper overflow the grid so a partial next card
  // shows. We push the overflow width into Swiper as slidesOffsetAfter so the
  // last page stays pinned to the grid edge instead of sliding into overflow.
  useEffect(() => {
    const updateSwiper = (offset: number) => {
      const swiper = swiperRef.current;
      if (!swiper || typeof swiper.update !== 'function') return;
      if (swiper.params.slidesOffsetAfter !== offset) {
        swiper.params.slidesOffsetAfter = offset;
        swiper.update();
      }
    };

    if (!desktopPeeking) {
      updateSwiper(0);
      return;
    }

    const wrapperEl = swiperWrapperRef.current;
    const gridEl = wrapperEl?.parentElement;
    if (!wrapperEl || !gridEl) return;

    const applyOffset = () => {
      const overflow = Math.max(wrapperEl.offsetWidth - gridEl.clientWidth, 0);
      updateSwiper(overflow);
    };

    applyOffset();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(applyOffset);
    observer.observe(gridEl);
    observer.observe(wrapperEl);
    return () => observer.disconnect();
  }, [desktopPeeking, visualSlideCount, desktopCardsNum]);

  // Detect AEM Edit mode synchronously on first render, then observe for live Edit↔Preview toggle.
  const [isEditMode, setIsEditMode] = useState(() =>
    typeof window !== 'undefined' && (document.documentElement.getAttribute('class') ?? '').includes('Edit')
  );

  useEffect(() => {
    const htmlEl = document.documentElement;

    const syncEditMode = () => {
      const wcmClass = htmlEl.getAttribute('class') ?? '';
      setIsEditMode(wcmClass.includes('Edit'));
    };

    syncEditMode();

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          syncEditMode();
        }
      });
    });

    observer.observe(htmlEl, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Forces Swiper to recalculate slide widths when container resizes (e.g. DevTools viewport toggle).
  useEffect(() => {
    const swiper = swiperRef.current;
    const wrapperEl = swiperWrapperRef.current;
    if (!swiper || !wrapperEl) return;
    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      swiper.update();
    });
    ro.observe(wrapperEl);

    return () => ro.disconnect();
  }, []);

  const handleSlideChange = (swiper: SwiperType) => setCurrentSlide(swiper.activeIndex);

  const renderSlotSlides = () =>
    renderedSlotKeys.map(key => {
      const slotModel = authoredItems[key];
      const slotItems = slotModel?.[':items'] ?? slotModel?.cqItems ?? {};
      const slotOrder = slotModel?.[':itemsOrder'] ?? slotModel?.cqItemsOrder ?? Object.keys(slotItems);

      return (
        <SwiperSlide key={key}>
          <Styled.SlideContent>
            <MSlot
              pagePath={resolvedPagePath}
              itemPath={`${resolvedItemPath}/${key}`}
              cqItems={slotItems}
              model={
                slotModel
                  ? {
                      ...slotModel,
                      ':order': slotOrder,
                    }
                  : undefined
              }
              isAuthorMode={isAuthorMode}
              aemReactComponentMapping={aemReactComponentMapping}
            />
          </Styled.SlideContent>
        </SwiperSlide>
      );
    });

  const renderChildSlides = () =>
    childSlides.map((child, index) => (
      <SwiperSlide key={index}>
        <Styled.SlideContent>{child}</Styled.SlideContent>
      </SwiperSlide>
    ));

  if (!hasHeader && !hasSlides) {
    return null;
  }

  return (
    <Styled.Wrapper className={className} data-component-name={COMPONENT_NAME} data-testid={TEST_ID} $peeking={desktopPeeking}>
      <Styled.GridContainer className="container px-0">
        {resolvedMContentHeader && (
          <Styled.HeaderWrapper>
            <MContentHeader
              alignment={
                resolvedMContentHeader.alignment === 'Centered'
                  ? MContentHeaderAlignment.Centered
                  : MContentHeaderAlignment.Left
              }
              mTextBlockSimple={resolvedMContentHeader.mTextBlockSimple}
              cta={resolvedMContentHeader.cta}
              secondaryCta={
                resolvedMContentHeader.mContentSwap?.button2 != null
                  ? (resolvedMContentHeader.mContentSwap.button2 as 'true' | 'false')
                  : undefined
              }
              mButton={resolveRawButton(resolvedMContentHeader.mButton, resolvedMContentHeader.mContentSwap?.mButton)}
              mButton2={resolveRawButton(resolvedMContentHeader.mButton2, resolvedMContentHeader.mContentSwap?.mButton2)}
            />
          </Styled.HeaderWrapper>
        )}

        {hasSlides && (
          <Styled.CarouselContainer $peeking={desktopPeeking}>
            <Styled.SwiperWrapper ref={swiperWrapperRef} $desktopCards={desktopCardsNum} $peeking={desktopPeeking} $isEditMode={isEditMode}>
              <Swiper
                // visualSlideCount forces a remount when slides are added/removed;
                // Swiper's observer doesn't reliably update pagination and watchOverflow
                // for dynamic child count changes despite config remaining identical.
                key={`${direction}-${desktopCardsNum}-${desktopPeeking ? 'p' : 'np'}-${visualSlideCount}-${
                  isDesktopSingleCard ? 's' : 'm'
                }-${isTwoSlideTabletGrid ? 't2' : 'tn'}`}
                dir={direction}
                modules={[Navigation, Pagination, A11y]}
                spaceBetween={isDesktopSingleCard ? 0 : 16}
                slidesPerView={isDesktopSingleCard ? 1 : getDesktopPerView(desktopCardsNum, desktopPeeking)}
                observer
                observeParents
                resizeObserver
                watchOverflow
                onSwiper={(swiper: SwiperType) => {
                  swiperRef.current = swiper;
                  syncSwiperState(swiper);
                }}
                onSlideChange={handleSlideChange}
                onBreakpoint={syncSwiperState}
                onResize={syncSwiperState}
                breakpoints={{
                  320: {
                    slidesPerView: isOnlyOneSlide ? 1 : getMobilePerView(),
                    spaceBetween: isOnlyOneSlide ? 0 : 16,
                  },
                  768: {
                    slidesPerView: isOnlyOneSlide ? 1 : isTwoSlideTabletGrid ? 2 : getTabletPerView(),
                    spaceBetween: isOnlyOneSlide ? 0 : 24,
                  },
                  992: {
                    slidesPerView: isDesktopSingleCard ? 1 : getDesktopPerView(desktopCardsNum, desktopPeeking),
                    spaceBetween: isDesktopSingleCard ? 0 : 24,
                  },
                }}
              >
                {hasChildren ? renderChildSlides() : renderSlotSlides()}
              </Swiper>
            </Styled.SwiperWrapper>

            {resolvedMCarouselControls && showControls && (
              <Styled.ControlsWrapper
                $showOnMobile={needsPagingMobile}
                $showOnTablet={needsPagingTablet}
                $showOnDesktop={needsPagingDesktop}
              >
                <MCarouselControls
                  type={controlsType}
                  arrows={showArrows}
                  imageOverlay={imageOverlay}
                  totalSlides={totalControlSlides}
                  currentSlide={currentControlSlide}
                  onPrev={handlePrev}
                  onNext={handleNext}
                />
              </Styled.ControlsWrapper>
            )}
          </Styled.CarouselContainer>
        )}
      </Styled.GridContainer>
    </Styled.Wrapper>
  );
};

export * from './OCardCarousel.types';
